// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import {
  dappAPI,
  listAccounts,
  onAccountsChanged,
  removeOnAccountsChanged,
} from '@canton-network/dapp-sdk';
import { useEffect, useMemo, useState } from 'react';

export const useAccounts = (isConnected: boolean = false) => {
  const [isFetching, setFetching] = useState(isConnected);
  const [accounts, setAccounts] = useState<dappAPI.Wallet[] | undefined>();

  useEffect(() => {
    if (!isConnected) {
      setAccounts(undefined);
      setFetching(false);
      return () => {};
    }

    let isActive = true;
    setFetching(true);

    const listener = (event: dappAPI.AccountsChangedEvent) => {
      if (isActive) setAccounts(event);
    };

    onAccountsChanged(listener).catch(console.error);

    listAccounts()
      .then((wallets) => {
        if (isActive) setAccounts(wallets);
      })
      .catch((err) => {
        console.error('Error requesting wallets:', err);
      })
      .finally(() => {
        if (isActive) setFetching(false);
      });

    return () => {
      isActive = false;
      removeOnAccountsChanged(listener).catch(console.error);
    };
  }, [isConnected]);

  const primaryAccount = useMemo(() => accounts?.find((a) => a.primary), [accounts]);

  return {
    primaryAccount,
    accounts,
    isLoading: isFetching || accounts === undefined,
  };
};
